import { Command } from "cmdk";
import { Check, ListPlus, Plus } from "lucide-react";
import type { ReactNode } from "react";

import { useCan } from "@/app/session/permissions";
import { useT } from "@/shared/i18n/useT";
import { THEME_OPTIONS } from "@/shared/theme/options";
import { useTheme } from "@/shared/theme/useTheme";

type Run = (action: () => void) => () => void;

export function PaletteItem({
  value,
  keywords,
  onSelect,
  children,
}: {
  value: string;
  keywords?: string[];
  onSelect: () => void;
  children: ReactNode;
}) {
  return (
    <Command.Item
      value={value}
      keywords={keywords}
      onSelect={onSelect}
      className="flex h-10 cursor-pointer items-center gap-3 rounded-lg px-2.5 text-[14.5px] text-ink data-[selected=true]:bg-surface-hover [&>svg]:size-4 [&>svg]:text-ink-muted"
    >
      {children}
    </Command.Item>
  );
}

/** Light, dark or the system's theme, matched by "theme" and the choice in either language. */
export function PaletteThemeItems({ run }: { run: Run }) {
  const t = useT();
  const { preference, setPreference } = useTheme();
  return THEME_OPTIONS.map(({ value, labelId, icon: Icon }) => (
    <PaletteItem
      key={value}
      value={`theme ${value}`}
      keywords={[t("theme.title"), t(labelId)]}
      onSelect={run(() => {
        setPreference(value);
      })}
    >
      <Icon aria-hidden="true" />
      <span className="flex-1">{t(`palette.theme.${value}`)}</span>
      {preference === value ? (
        <>
          <Check className="text-brand-strong!" aria-hidden="true" />
          <span className="sr-only">{t("palette.theme.current")}</span>
        </>
      ) : null}
    </PaletteItem>
  ));
}

interface PaletteCreateItemsProps {
  run: Run;
  onNewProject: () => void;
  onNewTask: () => void;
}

/** New task and new project, each only for people who may create one. */
export function PaletteCreateItems({ run, onNewProject, onNewTask }: PaletteCreateItemsProps) {
  const t = useT();
  const canCreateTasks = useCan("tasks.create");
  const canCreateProjects = useCan("projects.create");
  return (
    <>
      {canCreateTasks ? (
        <PaletteItem value="new task" onSelect={run(onNewTask)}>
          <ListPlus aria-hidden="true" />
          {t("palette.newTask")}
        </PaletteItem>
      ) : null}
      {canCreateProjects ? (
        <PaletteItem value="new project" onSelect={run(onNewProject)}>
          <Plus aria-hidden="true" />
          {t("palette.newProject")}
        </PaletteItem>
      ) : null}
    </>
  );
}
